// CustomFieldsSection — reusable Ship 3 custom-field editor for matter +
// contact edit forms.  Fetches the current field defs + values via the
// scope-specific endpoint, renders one input per field grouped by set,
// tracks per-field dirty state via Object.hasOwn presence (HIGH-5), and
// submits ONLY the touched fields on save.
//
// Props:
//   entityType — 'matter' | 'contact'
//   entityId   — number (required — no-op when null/undefined)
//   canWrite   — boolean; when false, inputs are read-only and Save is hidden
//
// Server contract (see api/src/routes/customFields.js §934-1062):
//   GET  /api/custom-fields/{matters|contacts}/:id/custom-fields
//        → { fields: [ {def_id, def_name, field_type, options, is_required,
//                       set_id, set_name, set_sort_order, def_sort_order,
//                       value_raw, value_display}, ... ] }
//     - Reads already filter admin_only sets for non-admin viewers, so we
//       don't get any field we couldn't write anyway (except the matter
//       ACL denial path, which returns 404).
//   PUT  /api/custom-fields/{matters|contacts}/:id/custom-fields
//        body: { values: { <field_id_string>: <value> } }
//        - Object.hasOwn semantics — only present keys are touched.
//        - value === null → CLEAR the field (DELETE row).
//        - value === '' → per-type: text/textarea keep, number/currency/
//                         dropdown/date/url/email → CLEAR (send null).
//        - Response: { updated_count, changed_field_ids } | { error, errors }.
//
// Panel notes folded in from Ship 3 spec:
//   HIGH-5  — Object.hasOwn is the presence test; NOT truthy — empty string
//             on text is still a real value.
//   HIGH-7  — dropdown accepts an unchanged inactive option; UI includes
//             the stored option in the picker even when its is_active=false,
//             suffixed "(inactive)" via resolveOptionLabel().
//   CRIT-3  — server already gates admin_only visibility on both read + write,
//             so no client-side re-check is needed here (defence-in-depth
//             lives server-side).
//
// The section renders nothing (returns null) when no defs exist for this
// scope — no clutter on firms that haven't configured custom fields yet.

import { useEffect, useMemo, useState, useCallback } from 'react'
import api from '../services/api'

const TEXT_KEEP_EMPTY = new Set(['text', 'textarea'])
const CLEAR_ON_EMPTY  = new Set(['number', 'currency', 'dropdown', 'date', 'url', 'email'])

// Mirror api/src/lib/customFieldsValidator.js:resolveOptionLabel — keep in
// sync when the server-side helper changes.  Isolated locally so the
// component ships without a shared-lib import dep.
function resolveOptionLabel (options, optionId) {
  if (!Array.isArray(options) || optionId == null) return ''
  const hit = options.find(o => o && o.id === optionId)
  if (!hit) return '(deleted option)'
  return hit.is_active ? hit.label : `${hit.label} (inactive)`
}

// Format a raw stored value for placement into a controlled input.  Every
// field type gets a stringified representation the input can render, EXCEPT
// checkbox which stays boolean.
function rawToInputValue (fieldType, raw) {
  if (raw === null || raw === undefined) {
    if (fieldType === 'checkbox') return false
    return ''
  }
  if (fieldType === 'checkbox') return Boolean(raw)
  if (fieldType === 'number' || fieldType === 'currency') {
    return typeof raw === 'number' ? String(raw) : String(raw ?? '')
  }
  // text/textarea/url/email/dropdown/date all render as strings.
  return String(raw)
}

// Convert an in-form input value into the PUT-body shape per spec HIGH-5.
function inputValueToPutBody (fieldType, inputVal) {
  if (fieldType === 'checkbox') return Boolean(inputVal)
  if (fieldType === 'number' || fieldType === 'currency') {
    if (inputVal === '' || inputVal === null || inputVal === undefined) return null
    const n = Number(inputVal)
    return Number.isFinite(n) ? n : null
  }
  const s = inputVal == null ? '' : String(inputVal)
  if (s === '') {
    if (TEXT_KEEP_EMPTY.has(fieldType)) return ''
    if (CLEAR_ON_EMPTY.has(fieldType))  return null
    return null
  }
  return s
}

// Group fields by set_id preserving the server's sort order (which already
// applies set_sort_order → set_id → def_sort_order → def_id).
function groupBySet (fields) {
  const groups = []
  const byId = new Map()
  for (const f of fields) {
    let bucket = byId.get(f.set_id)
    if (!bucket) {
      bucket = { set_id: f.set_id, set_name: f.set_name, fields: [] }
      byId.set(f.set_id, bucket)
      groups.push(bucket)
    }
    bucket.fields.push(f)
  }
  return groups
}

export default function CustomFieldsSection ({ entityType, entityId, canWrite }) {
  if (entityType !== 'matter' && entityType !== 'contact') {
    // Defensive — component should never be mounted with any other value.
    throw new Error(`CustomFieldsSection: invalid entityType '${entityType}'`)
  }

  const [fields, setFields] = useState([])
  const [values, setValues] = useState({})          // {def_id_string: inputValue}
  const [touched, setTouched] = useState({})        // {def_id_string: true} — Object.hasOwn presence
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState('')
  const [fieldErrors, setFieldErrors] = useState({}) // {def_id_string: msg}
  const [savedFlash, setSavedFlash]   = useState('')

  const endpointBase = entityType === 'matter'
    ? `/api/custom-fields/matters/${entityId}/custom-fields`
    : `/api/custom-fields/contacts/${entityId}/custom-fields`

  const load = useCallback(async () => {
    if (!entityId) return
    setLoading(true); setError(''); setFieldErrors({})
    try {
      const r = await api.get(endpointBase)
      const arr = Array.isArray(r.data?.fields) ? r.data.fields : []
      setFields(arr)
      // Seed values from value_raw per field.  This becomes the pristine
      // baseline; touched will re-populate as the user edits.
      const seeded = {}
      for (const f of arr) {
        seeded[String(f.def_id)] = rawToInputValue(f.field_type, f.value_raw)
      }
      setValues(seeded)
      setTouched({})
    } catch (err) {
      // Pre-API-restart graceful degrade — mirrors MatterBlockListSection.
      if (err.response?.status === 404 || err.response?.status === 405) {
        setFields([])
      } else {
        setError(err.response?.data?.error || 'Could not load custom fields')
      }
    } finally {
      setLoading(false)
    }
  }, [endpointBase, entityId])

  useEffect(() => { load() }, [load])

  const groups = useMemo(() => groupBySet(fields), [fields])

  const handleChange = (defId, nextInputValue) => {
    const key = String(defId)
    setValues(prev => ({ ...prev, [key]: nextInputValue }))
    setTouched(prev => Object.hasOwn(prev, key) ? prev : { ...prev, [key]: true })
    if (fieldErrors[key]) {
      setFieldErrors(prev => {
        const copy = { ...prev }
        delete copy[key]
        return copy
      })
    }
  }

  const anyDirty = Object.keys(touched).length > 0

  // Client-side required check — server also enforces via validator.  Only
  // block save when a touched required field ends up empty; we don't block
  // untouched requireds (those may have been valid when stored + we shouldn't
  // force the user to fix unrelated fields to save the one they edited).
  const requiredIssues = useMemo(() => {
    const issues = {}
    for (const f of fields) {
      const key = String(f.def_id)
      if (!Object.hasOwn(touched, key)) continue
      if (!f.is_required) continue
      const put = inputValueToPutBody(f.field_type, values[key])
      // Checkbox: no meaningful "required" (false is a valid answer).  Text
      // types where empty-is-valid: allow empty too (matches server contract).
      if (f.field_type === 'checkbox') continue
      const isEmpty = (put === null) || (put === '' && !TEXT_KEEP_EMPTY.has(f.field_type))
      if (isEmpty) {
        issues[key] = `${f.def_name} is required`
      }
    }
    return issues
  }, [fields, values, touched])

  const handleSave = async (e) => {
    e?.preventDefault?.()
    if (!canWrite || !anyDirty || saving) return
    setError(''); setFieldErrors({}); setSavedFlash('')

    if (Object.keys(requiredIssues).length > 0) {
      setFieldErrors(requiredIssues)
      setError('Please fill in every required field before saving.')
      return
    }

    // Build touched-flag PUT body — Object.hasOwn presence, not truthy.
    const body = { values: {} }
    for (const key of Object.keys(touched)) {
      if (!Object.hasOwn(touched, key)) continue
      const f = fields.find(x => String(x.def_id) === key)
      if (!f) continue
      body.values[key] = inputValueToPutBody(f.field_type, values[key])
    }
    if (Object.keys(body.values).length === 0) return

    setSaving(true)
    try {
      const r = await api.put(endpointBase, body)
      const updated = r.data?.updated_count ?? 0
      setSavedFlash(updated > 0 ? `Saved ${updated} field${updated === 1 ? '' : 's'}.` : 'No changes.')
      // Reload so any server-side derived state (updated_at, backfills,
      // trigger effects) is reflected in the UI.
      await load()
      // Flash clears after a moment.
      setTimeout(() => setSavedFlash(''), 2500)
    } catch (err) {
      const data = err.response?.data
      if (data?.errors && typeof data.errors === 'object') {
        // Per-field error map — coerce ids to string keys.
        const perField = {}
        for (const [id, msg] of Object.entries(data.errors)) {
          perField[String(id)] = String(msg)
        }
        setFieldErrors(perField)
        setError(data.error || 'One or more fields were rejected.')
      } else {
        setError(data?.error || 'Could not save custom fields')
      }
    } finally {
      setSaving(false)
    }
  }

  // No entity yet OR nothing to render.
  if (!entityId) return null
  if (!loading && fields.length === 0) return null

  return (
    <div className="md:col-span-3 border-2 border-indigo-200 bg-indigo-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-indigo-900">🧩 Custom Fields</h3>
          <p className="text-xs text-indigo-800 mt-1">
            Firm-configured fields for this {entityType}. Only edited fields are saved.
          </p>
        </div>
        {canWrite && anyDirty && (
          <span className="text-[11px] text-indigo-700 italic">
            {Object.keys(touched).length} unsaved change{Object.keys(touched).length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 mb-3">
          {error}
        </div>
      )}
      {savedFlash && (
        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700 mb-3">
          ✓ {savedFlash}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-indigo-800 italic">Loading fields…</div>
      ) : (
        <div className="space-y-4">
          {groups.map(group => (
            <div key={group.set_id} className="bg-white rounded-lg p-3 border border-indigo-100">
              <div className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                {group.set_name}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {group.fields.map(f => (
                  <FieldRenderer
                    key={f.def_id}
                    field={f}
                    value={values[String(f.def_id)]}
                    onChange={(v) => handleChange(f.def_id, v)}
                    error={fieldErrors[String(f.def_id)]}
                    canWrite={canWrite}
                  />
                ))}
              </div>
            </div>
          ))}

          {canWrite && (
            <div className="flex gap-2 items-center">
              <button
                type="button"
                onClick={handleSave}
                disabled={!anyDirty || saving}
                className="text-xs bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded"
              >
                {saving ? 'Saving…' : 'Save Custom Fields'}
              </button>
              {anyDirty && !saving && (
                <button
                  type="button"
                  onClick={() => { load() }}
                  className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded"
                >
                  Discard changes
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// -------------------------------------------------------------------------
// FieldRenderer — one row: label + input per field_type + inline error.
// -------------------------------------------------------------------------
function FieldRenderer ({ field, value, onChange, error, canWrite }) {
  const { def_id, def_name, field_type, options, is_required, value_raw } = field
  const inputId = `cf-${def_id}`
  const disabled = !canWrite

  const label = (
    <label htmlFor={inputId} className="block text-xs font-medium text-gray-700 mb-1">
      {def_name}
      {is_required && <span className="text-red-600 ml-0.5" aria-label="required">*</span>}
    </label>
  )

  const inputClass = 'w-full border rounded px-2 py-1 text-sm ' +
    (error ? 'border-red-400 bg-red-50 ' : 'border-gray-300 ') +
    (disabled ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'focus:outline-none focus:ring-2 focus:ring-indigo-500')

  let inputEl = null
  if (field_type === 'text') {
    inputEl = (
      <input id={inputId} type="text" value={value ?? ''}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        className={inputClass} />
    )
  } else if (field_type === 'textarea') {
    inputEl = (
      <textarea id={inputId} value={value ?? ''} rows={3}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        className={inputClass} />
    )
  } else if (field_type === 'number') {
    inputEl = (
      <input id={inputId} type="number" step="any" value={value ?? ''}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        className={inputClass} />
    )
  } else if (field_type === 'currency') {
    inputEl = (
      <div className="relative">
        <span className="absolute left-2 top-1.5 text-sm text-gray-500 pointer-events-none">$</span>
        <input id={inputId} type="number" step="0.01" value={value ?? ''}
          onChange={e => onChange(e.target.value)} disabled={disabled}
          className={inputClass + ' pl-6'} />
      </div>
    )
  } else if (field_type === 'date') {
    inputEl = (
      <input id={inputId} type="date" value={value ?? ''}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        className={inputClass} />
    )
  } else if (field_type === 'dropdown') {
    const optsArr = Array.isArray(options) ? options : []
    const activeOptions = optsArr.filter(o => o && o.is_active)
    // HIGH-7 — include the stored option in the picker even when inactive,
    // so an unchanged inactive value can still be saved (server accepts it).
    // The stored value is the value_raw (original DB value), not the current
    // in-form `value` — comparing to value would let the option vanish as
    // soon as the user picks something else.
    const storedIsInactive = value_raw != null &&
      !activeOptions.some(o => o.id === value_raw) &&
      optsArr.some(o => o.id === value_raw)
    const inactiveStoredOpt = storedIsInactive
      ? optsArr.find(o => o.id === value_raw)
      : null
    // Handle "(deleted option)" fallback — if the DB has a value that no
    // longer resolves to ANY option (rare, only from hand-patching), still
    // render it as an option so the picker matches the value + doesn't
    // silently drop it.  Selecting anything else replaces it.
    const orphanValue = value_raw != null && !optsArr.some(o => o.id === value_raw)
      ? value_raw : null
    inputEl = (
      <select id={inputId} value={value ?? ''}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        className={inputClass}>
        <option value="">{is_required ? '— select —' : '(none)'}</option>
        {activeOptions.map(o => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
        {inactiveStoredOpt && (
          <option key={inactiveStoredOpt.id} value={inactiveStoredOpt.id}>
            {resolveOptionLabel(optsArr, inactiveStoredOpt.id)}
          </option>
        )}
        {orphanValue && (
          <option key={orphanValue} value={orphanValue}>
            {resolveOptionLabel(optsArr, orphanValue)}
          </option>
        )}
      </select>
    )
  } else if (field_type === 'checkbox') {
    inputEl = (
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input id={inputId} type="checkbox" checked={Boolean(value)}
          onChange={e => onChange(e.target.checked)} disabled={disabled}
          className="rounded border-gray-300" />
        <span>Yes</span>
      </label>
    )
  } else if (field_type === 'url') {
    inputEl = (
      <input id={inputId} type="url" value={value ?? ''}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        placeholder="https://…"
        className={inputClass} />
    )
  } else if (field_type === 'email') {
    inputEl = (
      <input id={inputId} type="email" value={value ?? ''}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        placeholder="name@example.com"
        className={inputClass} />
    )
  } else {
    // Unknown field type — render as read-only text so nothing crashes.
    inputEl = (
      <input id={inputId} type="text" value={value ?? ''} disabled
        className={inputClass} />
    )
  }

  return (
    <div>
      {field_type === 'checkbox' ? null : label}
      {field_type === 'checkbox' ? (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">
            {def_name}
            {is_required && <span className="text-red-600 ml-0.5" aria-label="required">*</span>}
          </div>
          {inputEl}
        </div>
      ) : inputEl}
      {error && (
        <div className="mt-1 text-[11px] text-red-600">{error}</div>
      )}
    </div>
  )
}
